import dayjs from 'dayjs';
import { mutate } from 'swr';

import type {
  CleanServiceItemAddRequest,
  CleanServiceItemEdit,
  PriceInfo,
} from '../../sharedTypes';
import { getCleanKindData } from '../helper';
import type {
  FreeItemTableData,
  InformationFormDataType,
  PaidItemTableData,
} from '../types';
import type { CleanKindAddRequest, CleanKindEditRequest } from './type';

export const initInformationFormData = {
  cleanKindName: '',
  subscriptionPlanIdList: [],
  description: '',
  included: '',
  warning: '',
  picture: [],
  janitor: null,
  partnerName: null,
  dailyReservationLimitDefault: null,
  reservationLimitRule: null,
  limitRuleValue: null,
  limitRuleUnit: null,
};

const formatPaidItems = (args: {
  type: 'add' | 'edit';
  paidItems: PaidItemTableData[];
  cleanKindId?: string;
}) => {
  const { type, paidItems, cleanKindId } = args;

  const formattedPaidItems = paidItems.map((item) => {
    const {
      itemName,
      taiwanPayPrice,
      pointPrice,
      reservationLimits: { reservationLowerLimit, reservationUpperLimit },
    } = item;

    const kind: PriceInfo['kind'] =
      typeof taiwanPayPrice === 'number' && typeof pointPrice === 'number'
        ? 'Both'
        : typeof taiwanPayPrice === 'number' && typeof pointPrice !== 'number'
        ? 'TaiwanPay'
        : 'Point';

    let priceInfo: PriceInfo = {
      kind,
      taiwanPayPrice: taiwanPayPrice ?? 0,
      pointPrice: pointPrice ?? 0,
      reservationLowerLimit: reservationLowerLimit ?? 0,
      reservationUpperLimit: reservationUpperLimit ?? 0,
    };

    if (kind === 'TaiwanPay') {
      priceInfo = {
        kind,
        taiwanPayPrice: taiwanPayPrice ?? 0,
        reservationLowerLimit: reservationLowerLimit ?? 0,
        reservationUpperLimit: reservationUpperLimit ?? 0,
      };
    }
    if (kind === 'Point') {
      priceInfo = {
        kind,
        pointPrice: pointPrice ?? 0,
        reservationLowerLimit: reservationLowerLimit ?? 0,
        reservationUpperLimit: reservationUpperLimit ?? 0,
      };
    }

    const _id = item.itemId.includes('item') ? null : item.itemId;

    if (type === 'add') {
      const formattedItem: CleanServiceItemAddRequest = {
        name: itemName,
        priceInfo,
      };

      return formattedItem;
    }

    const formattedItem = {
      ...item,
      _id,
      cleanKindId: cleanKindId ?? '',
      display: item.display ?? true,
      createdTime: item.createdTime ?? dayjs().valueOf(),
      updatedTime: dayjs().valueOf(),
      name: itemName,
      priceInfo,
    };

    return formattedItem;
  });

  return formattedPaidItems;
};

export const formatAddCleanKindRequestData = (
  formValues: InformationFormDataType,
  cleanKindItems: {
    freeItem: FreeItemTableData[];
    paidItems: PaidItemTableData[];
  }
) => {
  const {
    cleanKindName,
    picture,
    janitor,
    partnerName,
    dailyReservationLimitDefault,
    reservationLimitRule,
    limitRuleValue,
    limitRuleUnit,
  } = formValues;
  const { freeItem, paidItems } = cleanKindItems;

  const { itemName, ticketId } = freeItem[0];
  const cleanServiceItems: CleanServiceItemAddRequest[] = [];

  if (ticketId.length !== 0) {
    cleanServiceItems.push({
      name: itemName,
      priceInfo: {
        kind: 'Free',
        availableTicketId: ticketId,
      },
    });
  }

  let formattedPaidItems: CleanServiceItemAddRequest[] = [];

  if (paidItems[0].itemName) {
    formattedPaidItems = formatPaidItems({ type: 'add', paidItems });
  }

  const cleanServiceItemList = cleanServiceItems.concat(formattedPaidItems);

  const data: CleanKindAddRequest = {
    ...formValues,
    name: cleanKindName,
    picture: picture.length !== 0 ? picture[0].showPath : null,
    janitor:
      janitor === 'Contractor'
        ? { kind: janitor }
        : { kind: janitor ?? 'Partner', partnerId: partnerName ?? '' },
    display: true,
    dailyReservationLimitDefault: dailyReservationLimitDefault ?? 0,
    memberReservationLimit:
      reservationLimitRule === 'None'
        ? { kind: reservationLimitRule }
        : {
            kind: reservationLimitRule ?? 'None',
            duration: limitRuleValue ?? 0,
            unit: limitRuleUnit ?? 'day',
          },
    cleanServiceItemList,
  };

  return data;
};

export const formatEditCleanKindRequestData = (args: {
  formValues: InformationFormDataType;
  cleanKindItems: {
    freeItem: FreeItemTableData[];
    paidItems: PaidItemTableData[];
  };
  cleanKindId: string | undefined;
}) => {
  const {
    formValues,
    cleanKindItems: { freeItem, paidItems },
    cleanKindId,
  } = args;

  const {
    cleanKindName,
    picture,
    janitor,
    partnerName,
    dailyReservationLimitDefault,
    reservationLimitRule,
    limitRuleValue,
    limitRuleUnit,
  } = formValues;

  const { itemName, ticketId } = freeItem[0];
  const cleanServiceItems: CleanServiceItemEdit[] = [];

  if (ticketId.length !== 0) {
    cleanServiceItems.push({
      ...freeItem[0],
      _id: freeItem[0]._id ?? null,
      cleanKindId: freeItem[0].cleanKindId ?? '',
      display: freeItem[0].display ?? true,
      createdTime: freeItem[0].createdTime ?? dayjs().valueOf(),
      updatedTime: dayjs().valueOf(),
      name: itemName,
      priceInfo: {
        kind: 'Free',
        availableTicketId: ticketId,
      },
    });
  }

  let formattedPaidItems: (
    | CleanServiceItemAddRequest
    | CleanServiceItemEdit
  )[] = [];

  if (paidItems[0].itemName) {
    formattedPaidItems = formatPaidItems({
      type: 'edit',
      paidItems,
      cleanKindId,
    });
  }

  const cleanServiceItemList = cleanServiceItems.concat(
    formattedPaidItems as CleanServiceItemEdit[]
  );

  const data: CleanKindEditRequest = {
    ...formValues,
    cleanKindId: cleanKindId ?? '',
    name: cleanKindName,
    picture: picture.length !== 0 ? picture[0].showPath : null,
    janitor:
      janitor === 'Contractor'
        ? { kind: janitor }
        : { kind: janitor ?? 'Partner', partnerId: partnerName ?? '' },
    display: true,
    dailyReservationLimitDefault: dailyReservationLimitDefault ?? 0,
    memberReservationLimit:
      reservationLimitRule === 'None'
        ? { kind: reservationLimitRule }
        : {
            kind: reservationLimitRule ?? 'None',
            duration: limitRuleValue ?? 0,
            unit: limitRuleUnit ?? 'day',
          },
    cleanServiceItemList,
  };

  return data;
};

export const fetchDataWithNewCleanKindId = async (
  newCleanKindId: string | undefined
) => {
  const url = `/ezcon/api/fetchCleanKind`;
  const cleanKindData = await getCleanKindData(url, newCleanKindId);
  mutate(url, cleanKindData);
};

export const excludeDummyItems = (
  cleanServiceItemList: CleanServiceItemEdit[]
) => {
  const dummyDataExcludedItemList = cleanServiceItemList.filter((item) => {
    const { name, priceInfo } = item;
    const { kind } = priceInfo;
    switch (kind) {
      case 'TaiwanPay':
        return (
          name &&
          typeof priceInfo.taiwanPayPrice === 'number' &&
          typeof priceInfo.reservationLowerLimit === 'number' &&
          typeof priceInfo.reservationUpperLimit === 'number'
        );
      case 'Point':
        return (
          name &&
          typeof priceInfo.pointPrice === 'number' &&
          typeof priceInfo.reservationLowerLimit === 'number' &&
          typeof priceInfo.reservationUpperLimit === 'number'
        );

      case 'Both':
        return (
          name &&
          typeof priceInfo.taiwanPayPrice === 'number' &&
          typeof priceInfo.pointPrice === 'number' &&
          typeof priceInfo.reservationLowerLimit === 'number' &&
          typeof priceInfo.reservationUpperLimit === 'number'
        );
      default:
        return true;
    }
  });

  return dummyDataExcludedItemList;
};
